/**
 * CinematicTriggerManager — Fires cinematic sequences based on map triggers.
 * Reads CinematicTrigger[] from the active map and listens to game events.
 * Instantiated in UIScene, destroyed on scene shutdown.
 */
import { EventBus, GameEvents } from '@/events/EventBus';
import { CinematicEvents } from './CinematicEvents';
import { CinematicDirector, getSequence } from './CinematicDirector';
import { getActiveMap } from '@/map/MapData';
import { TILE_SIZE } from '@/config/GameConfig';
import type { CinematicTrigger } from '@/map/MapDefs';

export class CinematicTriggerManager {
  private triggers: CinematicTrigger[] = [];
  private fired = new Set<string>(); // sequenceIds fired this session
  private tileCheckInterval: ReturnType<typeof setInterval> | null = null;
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    const map = getActiveMap();
    this.triggers = map.cinematics ?? [];
    if (this.triggers.length === 0) return;

    // Listen to game events
    EventBus.on(GameEvents.WAVE_STARTED, this.onWaveStarted, this);
    EventBus.on(GameEvents.WAVE_COMPLETED, this.onWaveCompleted, this);
    EventBus.on(GameEvents.GAME_OVER, this.onGameOver, this);

    // Tile enter checks — poll every 500ms during waves
    const tileTriggers = this.triggers.filter(t => t.event === 'tileEnter');
    if (tileTriggers.length > 0) {
      this.tileCheckInterval = setInterval(() => this.checkTileTriggers(tileTriggers), 500);
    }
  }

  /** Call once after scene is ready to fire mapStart triggers */
  fireMapStart(): void {
    this.fireTriggers('mapStart');
  }

  private onWaveStarted = (waveNum: number): void => {
    this.fireTriggers('waveStart', waveNum);
  };

  private onWaveCompleted = (waveNum: number): void => {
    this.fireTriggers('waveEnd', waveNum);
  };

  private onGameOver = (victory: boolean): void => {
    this.fireTriggers(victory ? 'victory' : 'defeat');
  };

  private fireTriggers(event: string, waveNum?: number): void {
    for (const trigger of this.triggers) {
      if (trigger.event !== event) continue;
      // Wave number filter
      if (trigger.waveNumber != null && trigger.waveNumber !== waveNum) continue;
      this.tryFire(trigger);
    }
  }

  private checkTileTriggers(tileTriggers: CinematicTrigger[]): void {
    // Skip if a cinematic is already playing
    if (this.scene.registry.get('cinematicActive')) return;

    const wm = this.scene.registry.get('waveManager') as { enemies: any[] } | undefined;
    const rm = this.scene.registry.get('robotManager') as { robots: any[] } | undefined;
    if (!wm && !rm) return;

    for (const trigger of tileTriggers) {
      if (!trigger.tile) continue;
      if (this.fired.has(trigger.sequenceId)) continue;

      const tx = trigger.tile.col * TILE_SIZE + TILE_SIZE / 2;
      const ty = trigger.tile.row * TILE_SIZE + TILE_SIZE / 2;
      const range = TILE_SIZE;

      // Check robots
      const robots = rm?.robots ?? [];
      for (const robot of robots) {
        if (!robot.alive) continue;
        const dx = robot.x - tx, dy = robot.y - ty;
        if (dx * dx + dy * dy < range * range) {
          this.tryFire(trigger);
          break;
        }
      }
      if (this.fired.has(trigger.sequenceId)) continue;

      // Check enemies
      const enemies = wm?.enemies ?? [];
      for (const enemy of enemies) {
        if (!enemy.alive) continue;
        const dx = enemy.x - tx, dy = enemy.y - ty;
        if (dx * dx + dy * dy < range * range) {
          this.tryFire(trigger);
          break;
        }
      }
    }
  }

  private tryFire(trigger: CinematicTrigger): void {
    const { sequenceId } = trigger;
    // Already fired this session
    if (this.fired.has(sequenceId)) return;
    // Don't fire while another cinematic is active
    if (this.scene.registry.get('cinematicActive')) return;
    // Sequence must exist
    if (!getSequence(sequenceId)) return;
    // Once check (default true)
    const once = trigger.once ?? true;
    if (once && !CinematicDirector.shouldShow(sequenceId)) return;

    this.fired.add(sequenceId);
    EventBus.emit(CinematicEvents.START, { sequenceId });
  }

  destroy(): void {
    EventBus.off(GameEvents.WAVE_STARTED, this.onWaveStarted, this);
    EventBus.off(GameEvents.WAVE_COMPLETED, this.onWaveCompleted, this);
    EventBus.off(GameEvents.GAME_OVER, this.onGameOver, this);
    if (this.tileCheckInterval) {
      clearInterval(this.tileCheckInterval);
      this.tileCheckInterval = null;
    }
  }
}
